import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { ChoiceCards } from './formKit';

/**
 * `ui:field: ChoiceCards` — a template's enum as selectable cards, the same
 * component the manifest-driven request page uses (./formKit), so "Deploy a
 * service" and "New environment" ask a choice the same way.
 *
 * Titles come from `enumNames`, and an optional `ui:options.hints` (an array in
 * enum order) adds the sentence under each:
 *
 *   start:
 *     type: string
 *     enum: [existing, new]
 *     enumNames: [I have already pushed it to GitHub, Start a new repository]
 *     ui:field: ChoiceCards
 *     ui:options:
 *       hints: [We check it and …, We scaffold …]
 *
 * Numbers work too (`tier: 1|2|3`): the value goes back with its schema type.
 */
export const ChoiceCardsField = (
  props: FieldExtensionComponentProps<string | number>,
) => {
  const { schema, uiSchema, formData, onChange, idSchema, rawErrors } = props;
  const values = (schema.enum ?? []) as Array<string | number>;
  const names = ((schema as { enumNames?: string[] }).enumNames ??
    []) as string[];
  const hints = ((uiSchema?.['ui:options'] as { hints?: string[] } | undefined)
    ?.hints ?? []) as string[];

  return (
    <div style={{ marginBottom: 8 }}>
      <ChoiceCards
        name={idSchema?.$id ?? 'choice'}
        label={schema.title || undefined}
        description={schema.description}
        value={formData ?? (schema.default as string | number | undefined)}
        onChange={value => onChange(value)}
        choices={values.map((value, i) => ({
          value,
          title: names[i] ?? String(value),
          hint: hints[i],
        }))}
      />
      {rawErrors?.length ? (
        <p
          role="alert"
          style={{
            color: 'var(--ar-err)',
            fontSize: '0.8125rem',
            fontWeight: 560,
            margin: '6px 0 0',
          }}
        >
          {rawErrors.join(' · ')}
        </p>
      ) : null}
    </div>
  );
};
